'use strict';

require('../../global/plugins/dibs-tooltip');

const $ = require('dibs-jquery-shim');
const Backbone = require('dibs-backbone');
const debounce = require('lodash.debounce');
const once = require('lodash.once');
const root = require('bunsen/helpers/windowCheck');
const mobileCheck = require('bunsen/helpers/mobileCheck');
const {localStorage, sessionStorage} = require('dibs-browser-storage');
const User = require('../../global/models/User');

const {provideContext, connectToStores} = require('fluxible-addons-react');

const authModalLoader = require('../../auth/authModalLoader').default;

const templates = require('../../templates/favorites/searches');
const calloutTracking = require('./calloutTracking');
const favoritesActions = require('../../global/actions/favoritesActions');
const CloseIcon = require('dibs-vg/dist/templateString/close');

// Stores
const StickyAlertTopStore = require('../StickyAlert/StickyAlertTop/StickyAlertTop-store');
const CalloutStore = require('./CalloutStore');
const PortfolioStore = require('../../global/stores/PortfolioStore');
const FavoritesStore = require('../../global/stores/FavoritesStore');

const CALLOUT_LIMIT = 3;
const CALLOUT_STOPPED_KEY = 'stopped';
const STORAGE_KEY = 'favoriteCallout';

const calloutHelper = {

    CALLOUT_TYPE_SEARCH_BOX: 'searchBox',
    CALLOUT_TYPE_FOLLOW_SEARCH: 'followSearch',
    CALLOUT_TYPE_FOLLOW_DEALER: 'followDealer',
    CALLOUT_TYPE_SAVE_ITEM: 'saveItem',
    CALLOUT_TYPE_EDIT_FOLDER: 'editFolder',
    CALLOUT_TYPE_REARRANGE_ITEMS: 'rearrangeItems',
    CALLOUT_TYPE_SELECT_ITEMS: 'selectItems',
    CALLOUT_TYPE_LISTED_NET_PRICE: 'listedNetPrice',
    CALLOUT_TYPE_CHECKOUT_SURVEY: 'checkoutSurvey',
    CALLOUT_TYPE_VIEW_INVOICE: 'viewInvoice',
    CALLOUT_TYPE_SAVE_TO_FOLDER: 'saveToFolder',

    _wasShownAnyCallout: false,

    connectToStore(component) {
        return provideContext(connectToStores(
            component,
            [CalloutStore],
            context => {
                const calloutStore = context.getStore(CalloutStore);

                return {
                    callout: calloutStore.getCalloutData()
                };
            }
        ));
    },

    /**
     * Checks callout shows limit,
     * was shown any type of callouts in this view.
     * Resets callout store after ajax page refresh.
     *
     * @return {Boolean}
     */
    canShowCallout({type}) {
        const wasShown = this._wasShown({type}); // this type callout
        const wasShownAnyCallout = this._wasShown(); // any type of callouts
        const isStopped = this._isStopped();

        return wasShown || wasShownAnyCallout || isStopped ? false : true;
    },

    stopCallouts() {
        const sessionData = sessionStorage.getItem(STORAGE_KEY) || {};
        sessionData[CALLOUT_STOPPED_KEY] = true;
        sessionStorage.setItem(STORAGE_KEY, sessionData);
    },

    saveAndTrack(payload) {
        this._wasShownAnyCallout = true;
        this.saveToStorage(payload);
        this.track(payload);
    },

    saveToStorage(payload) {
        const {type, turnOff} = payload;
        const {session, storage} = this._getSessionAndStorageData(type);

        storage[type]++;

        if (turnOff) {
            storage[type] = CALLOUT_LIMIT;
        }

        session[type] = true;
        this._setSessionAndStorageData({session, storage});
    },

    track(payload) {
        const {type} = payload;
        const trackCallout = calloutTracking[type];

        if (trackCallout && typeof trackCallout === 'function') {
            trackCallout();
        }
    },

    resetCallout() {
        this._wasShownAnyCallout = false;
    },

    /**
     * Enables Follow Search Callout after search form submit if Search Box Callout was shown
     */
    enableFollowSearchCallout() {
        const type = this.CALLOUT_TYPE_FOLLOW_SEARCH;
        this._enableCallout(type);
    },

    subscribeSearchSubmit() {
        $.subscribe('nav:search-performed', function () {
            calloutHelper.enableFollowSearchCallout();
        });
    },

    /**
     * Checks callout shows limit and peferred type
     * @return {Promise}
     */
    hasUserFavorites(payload) {
        const {type, favoriteType, context} = payload;
        const portfolioStore = context.getStore(PortfolioStore);
        const hasLocalSaves = portfolioStore.localItemsCount();

        if (hasLocalSaves) {
            calloutHelper.saveToStorage({type, turnOff: true});
            return Promise.resolve(true);
        }

        return context.executeAction(favoritesActions.fetchFavorites)
            .then(() => {
                const items = context.getStore(FavoritesStore).getFavoritesByType(favoriteType);
                if (items.length) {
                    calloutHelper.saveToStorage({type, turnOff: true});
                    return true;
                }
                return false;
            });
    },

    showSaveItemCallout(payload) {
        const {context, tooltipElement} = payload;
        const type = this.CALLOUT_TYPE_SAVE_ITEM;
        const productsResults = $('#productsResults');

        const closeCallback = function () {
            productsResults.removeClass('is-favorites-visible');
        };
        productsResults.addClass('is-favorites-visible');

        this.saveAndTrack({type});
        this._showSaveItemTooltip({tooltipElement, closeCallback, context});
    },

    showSaveToFolderCallout(payload) {
        const {context, tooltipElement} = payload;
        const type = this.CALLOUT_TYPE_SAVE_TO_FOLDER;

        this.saveAndTrack({type});
        this._showSaveToFolderTooltip({tooltipElement, context});
    },

    _getSessionAndStorageData(type) {
        const storageData = localStorage.getItem(STORAGE_KEY);
        const sessionData = sessionStorage.getItem(STORAGE_KEY);
        const storage = {
            [type]: 0
        };
        const session = {
            [type]: false
        };

        if (storageData) {
            Object.assign(storage, storageData);
        }

        if (sessionData) {
            Object.assign(session, sessionData);
        }

        return {session, storage};
    },

    _setSessionAndStorageData(payload) {
        const {session, storage} = payload;
        localStorage.setItem(STORAGE_KEY, storage);
        sessionStorage.setItem(STORAGE_KEY, session);
    },

    _showSaveItemTooltip(payload) {
        const {tooltipElement, closeCallback, context} = payload;
        const position = {
            my: 'left top',
            at: 'left bottom+40'
        };
        let templateVars = {
            title: 'Save Beautiful Things',
            content: 'Save this item to create a collection of your Favorites.',
            closeIcon: CloseIcon
        };

        if (mobileCheck.any()) {
            templateVars = {
                content: 'Tap to save in your favorites.'
            };
        }

        this._showTooltip({position, templateVars, tooltipElement, closeCallback, context});
    },

    _showSaveToFolderTooltip(payload) {
        const {tooltipElement, context} = payload;
        const position = {
            my: 'left top',
            at: 'left bottom+40'
        };
        const templateVars = {
            title: 'Save this item in a folder',
            content: 'Click the folder icon to save items into organized folders.',
            closeIcon: CloseIcon
        };

        this._showTooltip({position, templateVars, tooltipElement, closeCallback: null, context});
    },

    _showTooltip(payload) {
        const {position, templateVars, tooltipElement, closeCallback, context} = payload;
        const options = {
            position: {
                collision: 'fit',
                within: '#mainContent'
            },
            allwaysRepositionArrow: true
        };

        options.position = Object.assign(options.position, position);

        const tooltipView = this._getTooltip({templateVars}).render();

        this._createTooltip({
            tooltipElement: tooltipElement,
            closeCallback: closeCallback,
            tooltipView: tooltipView,
            options: options,
            context: context
        });
    },

    _getTooltip(payload) {
        const {templateVars} = payload;

        const TooltipView = Backbone.BaseView.extend({
            template: templates.ctaMomentumTooltip,
            templateVars: templateVars,
            events: {
                'click .js-close-tooltip': '_handleCloseTooltipClick'
            },
            _handleCloseTooltipClick: function (event) {
                event.preventDefault();
                this.trigger('close');
            }
        });

        return new TooltipView();
    },

    _createTooltip(payload) {
        const {tooltipElement, closeCallback, tooltipView, options, context} = payload;
        const stickyAlertTopStore = context.getStore(StickyAlertTopStore);
        const tooltipViewElement = tooltipView.el;
        const tooltipOptions = {
            content() {
                return tooltipViewElement;
            },
            items: tooltipElement,
            tooltipClass: 'dibstooltip-momentum',
            disabled: true
        };

        tooltipElement
            .on('mouseout focusout mouseleave', this._stopPropagation)
            .dibsTooltip(Object.assign(tooltipOptions, options))
            .dibsTooltip('disable')
            .dibsTooltip('open');

        this._disableLinkTooltip(tooltipElement.children());

        const tooltipClose = debounce(() => {
            try {
                tooltipElement.dibsTooltip('close');
                this._startPropagation(tooltipElement);
            } catch (e) {
                // nothing to catch
            }
        }, 500, true);

        const tooltipOpen = debounce(() => {
            try {
                tooltipElement.dibsTooltip('open');
                tooltipView.setElement(tooltipViewElement);
            } catch (e) {
                // nothing to catch
            }
        }, 500);

        root.addEventListener('resize', tooltipClose);
        root.addEventListener('resize', tooltipOpen);

        // Reposition when dfp scripts are loaded
        $.subscribe('dfp:script:loaded', () => {
            tooltipClose();
            tooltipOpen();
        });

        // Reposition when sticky alert top shows up
        stickyAlertTopStore.addChangeListener(tooltipClose);
        stickyAlertTopStore.addChangeListener(tooltipOpen);

        tooltipElement.children().one('click', () => {
            this._handleTooltipElementClick({tooltipElement, closeCallback});
        });

        this._subscribeTooltipClose({tooltipView, tooltipElement, closeCallback});
    },

    _stopPropagation(event) {
        // Stop propagation to disable auto fade out when tooltip loses focus
        event.stopImmediatePropagation();
    },

    _startPropagation(tooltipElement) {
        tooltipElement.off('mouseout focusout mouseleave', this._stopPropagation);
    },

    _disableLinkTooltip(tooltipElement) {
        try {
            tooltipElement.dibsTooltip('disable');
        } catch (e) {
            // nothing to catch
        }
    },

    _enableLinkTooltip(tooltipElement) {
        try {
            tooltipElement.dibsTooltip('enable');
        } catch (e) {
            // nothing to catch
        }
    },

    _handleTooltipElementClick(payload) {
        const deactivateTracking = once(this._deactivateTracking);
        calloutTracking.activateTracking(true);
        this._handleTooltipClose(payload);

        if (User.loggedIn()) {
            deactivateTracking();
        } else {
            authModalLoader.getLoadPromise().then(() => deactivateTracking());
        }
    },

    _deactivateTracking() {
        // Adding time out to make sure it gets correct save tracking object
        setTimeout(() => {
            calloutTracking.activateTracking(false);
        }, 1000);
    },

    _subscribeTooltipClose(payload) {
        const {tooltipView, tooltipElement, closeCallback} = payload;

        if (mobileCheck.any()) {
            const scrollToClose = 100; // scroll 100 pixels to close tooltip
            const positionToClose = $(root).scrollTop() + scrollToClose;
            root.addEventListener('scroll', () => {
                if ($(root).scrollTop() >= positionToClose) {
                    this._handleTooltipClose({tooltipElement, closeCallback});
                }
            });
        } else {
            const force = true;
            tooltipView.on('close', () => this._handleTooltipClose({tooltipElement, closeCallback, force}));

            root.addEventListener('click', event => {
                const isTooltip = !!$(event.target).closest('.ui-tooltip').length;
                if (isTooltip) {
                    return;
                }

                this._handleTooltipClose({tooltipElement, closeCallback});
            });
        }
    },

    _handleTooltipClose(payload) {
        const {tooltipElement, closeCallback, force} = payload;

        if (force) {
            calloutHelper.stopCallouts();
        }

        if (typeof closeCallback === 'function') {
            closeCallback();
        }

        try {
            tooltipElement.off('click', this._handleTooltipElementClick);
            tooltipElement.dibsTooltip('close');
            this._startPropagation(tooltipElement);

            this._enableLinkTooltip(tooltipElement.children());
        } catch (e) {
            // nothing to catch
        }
    },

    _wasShown(payload = {}) {
        const {type} = payload;

        if (type) {
            const storageData = localStorage.getItem(STORAGE_KEY);
            const sessionData = sessionStorage.getItem(STORAGE_KEY);

            return sessionData && sessionData[type] || storageData && storageData[type] >= CALLOUT_LIMIT;
        } else {
            return this._wasShownAnyCallout;
        }
    },

    _isStopped() {
        const sessionData = sessionStorage.getItem(STORAGE_KEY);
        if (sessionData) {
            return sessionData[CALLOUT_STOPPED_KEY] === true;
        }
        return false;
    },

    _enableCallout(type) {
        const {session, storage} = this._getSessionAndStorageData(type);
        const showsCount = storage[type];

        if (showsCount && showsCount > 0) {
            storage[type] = showsCount - 1;
        }

        session[type] = false;
        this._setSessionAndStorageData({session, storage});
    }
};

module.exports = calloutHelper;



// WEBPACK FOOTER //
// ./assets/js/components/Callout/calloutHelper.es.js